import { graphql } from "@apollo/client/react/hoc"
import Button from "@pathwright/ui/src/components/button/Button"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import useCountDown from "@pathwright/ui/src/components/hooks/useCountDown"
import useDidMountEffect from "@pathwright/ui/src/components/hooks/useDidMountEffect"
import withLauncher from "@pathwright/ui/src/components/launcher/withLauncher"
import Course from "@pathwright/ui/src/components/library/Course"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import ProgressBar from "@pathwright/ui/src/components/progress/ProgressBar"
import { useUIThemeContext } from "@pathwright/ui/src/components/ui/PathwrightUI"
import compose from "lodash/flowRight"
import get from "lodash/get"
import PropTypes from "prop-types"
import styled from "styled-components"
import { ResetPathItemDocument } from "../../api/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { getGroupUrl } from "../../utils/urls"
import { useResourceContext } from "../ResourceContext"
import withCreateRegistrationUpsertGroup from "../graphql/withCreateRegistrationUpsertGroup"
import * as PathUI from "../ui/PathUI"
import {
  getPathItemNonPassiveFutureStartsDate,
  isPathItemComplete,
  isPathItemInProgress
} from "../utils"
import LearnPathScheduleLabel from "./LearnPathScheduleLabel"

const StatusArea = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  border-radius: 6px;
  color: white;
  &:not(:empty) {
    padding: 5px;
    background: ${(p) =>
      p.layout === "grid" ? "rgba(255,255,255, .8);" : "rgba(0, 0, 0, 0.2)"};
  }
`
const ActionArea = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;

  html.no-touch
    li:not(:hover)
    &
    .SubmitButton--default:not(.LearnPathResourceButton--always-visible),
  html.no-touch
    li:not(:hover)
    &
    .Button:not(.SubmitButton):not(.LearnPathResourceButton--always-visible) {
    visibility: hidden;
  }
`
const Progress = styled(ProgressBar)`
  position: absolute;
  bottom: 10px;
  right: 10px;
  left: 10px;
  width: calc(100% - 20px);
  border-radius: 10px;
`
const Badge = styled(Pathicon)`
  position: absolute;
  bottom: 10px;
  left: 10px;
`

const LearnPathResource = ({
  pathItem,
  parentPathItem,
  layout,
  createRegistrationUpsertGroup,
  onLaunch
}) => {
  const { me } = usePathwrightContext()
  const { group: parentGroup, registration: parentGroupRegistration } =
    useResourceContext()
  const theme = useUIThemeContext()
  const nonPassiveFutureStartsDate = getPathItemNonPassiveFutureStartsDate(
    pathItem,
    parentPathItem,
    parentGroup
  )
  const dueForceUpdate = useCountDown(pathItem.due)
  const startsForceUpdate = useCountDown(nonPassiveFutureStartsDate)
  const { tc } = useTranslate()

  const masterUrl = getGroupUrl(
    pathItem.linked_resource.slug,
    get(pathItem, "linked_resource.curriculum_group.id")
  )

  const groupUrl = getGroupUrl(
    pathItem.linked_resource.slug,
    get(pathItem, "linked_cohort.id")
  )

  // link to the resource master
  const toMaster = {
    pathname: masterUrl,
    state: { setResourceLaunchedFrom: true }
  }

  // link ot the child cohort
  const toGroup = {
    pathname: groupUrl,
    query: {
      // link directly to the registration path
      path_id: get(pathItem, "linked_registration.path.id") || null
    },
    state: { setResourceLaunchedFrom: true }
  }

  // Create registration in child cohort if the user has an existing registration
  // in the collection.
  const createRegistration = !!parentGroupRegistration

  // the user's linked registration, should be same as pathItem.linked_registration for the learn path, but simply mimicking TeachPathResource
  const linkedUserRegistration = get(
    pathItem.linked_cohort,
    "user_registration"
  )

  useDidMountEffect(() => {
    // FWIW, linkedUserRegistration should always exist at this point
    if (linkedUserRegistration) {
      onLaunch(toGroup)
    }
  }, [linkedUserRegistration && linkedUserRegistration.id])

  return (
    <PathUI.PathResourceContainer layout={layout}>
      <Course
        course={pathItem.linked_resource}
        layout={layout}
        emphasis={pathItem.progress === 0 ? "primary" : "tertiary"}
      />
      <StatusArea layout={layout}>
        <LearnPathScheduleLabel pathItem={pathItem} />
      </StatusArea>
      {!nonPassiveFutureStartsDate && (
        <ActionArea>
          {parentGroup.is_curriculum ? (
            <Button
              brand
              styleType="primary"
              onClick={() => onLaunch(toMaster)}
            >
              {tc("View")}
            </Button>
          ) : !linkedUserRegistration ? (
            <SubmitButton
              brand
              styleType="primary"
              onClick={() => createRegistrationUpsertGroup(createRegistration)}
            >
              {tc("Start")}
            </SubmitButton>
          ) : isPathItemComplete(pathItem) ? (
            <Button brand styleType="primary" onClick={() => onLaunch(toGroup)}>
              {tc("Review")}
            </Button>
          ) : isPathItemInProgress(pathItem) ? (
            <Button
              className="LearnPathResourceButton--always-visible"
              brand
              styleType="primary"
              onClick={() => onLaunch(toGroup)}
              style={{ bottom: 10 }}
            >
              {tc("Resume")}
            </Button>
          ) : (
            <Button brand styleType="primary" onClick={() => onLaunch(toGroup)}>
              {tc("Start")}
            </Button>
          )}
        </ActionArea>
      )}
      {pathItem.progress === 1 && isPathItemComplete(pathItem) && (
        <Badge icon="check-circle" style={{ color: theme.primaryBrandColor }} />
      )}
      {isPathItemInProgress(pathItem) && (
        <Progress progress={pathItem.progress} />
      )}
    </PathUI.PathResourceContainer>
  )
}

LearnPathResource.displayName = "LearnPathResource"

LearnPathResource.propTypes = {
  pathItem: PropTypes.object.isRequired
}

export default compose(
  withLauncher,
  withCreateRegistrationUpsertGroup,
  // currently collection resource steps are completed automatically when their child path is completed
  // graphql(COMPLETE_PATH_ITEM_MUTATION, {
  //   props: ({ mutate, ownProps }) => ({
  //     completePathItem: () =>
  //       mutate({
  //         variables: {
  //           id: ownProps.pathItem.id,
  //           path_id: ownProps.pathItem.path_id,
  //           comment: "",
  //           skip: false
  //         }
  //       })
  //   })
  // }),
  graphql(ResetPathItemDocument, {
    props: ({ mutate, ownProps }) => ({
      resetPathItem: () =>
        mutate({
          variables: {
            id: ownProps.pathItem.id,
            path_id: ownProps.pathItem.path_id
          }
        })
    })
  })
)(LearnPathResource)
